import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Repeat } from 'lucide-react';
import SearchNoResults from '../components/SearchNoResults';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import BrandLogo from '../components/BrandLogo';
import VehicleBanner from '../components/VehicleBanner';
import SortSelect, { sortProducts, isBestPrice, useTopRatedFilter } from '../components/SortSelect';
import { productsAPI, cartAPI, categoriesAPI } from '../services/api';
import SearchFilters, { EMPTY_SEARCH_FILTERS, applySearchFilters, useSearchFilterParams } from '../components/SearchFilters';
import { vehicleEnergy } from '../services/fuel';
import { vehicleStore, useSelectedVehicle } from '../services/vehicleStore';
import { useT } from '../services/i18n';
import { usePageMeta } from '../services/seo';
import { searchTokens, matchesTokens } from '../components/SearchHighlight';
import { ga } from '../services/ga';
import { consent } from '../services/consent';

const SearchPage = () => {
  const { t, lang } = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get('q') || '').trim();
  const manufacturer = (searchParams.get('marque') || '').trim();
  const equivBrand = (searchParams.get('equiv') || '').trim();
  const vehicle = useSelectedVehicle();
  const [products, setProducts] = useState([]);
  const [compatibleIds, setCompatibleIds] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [sort, setSort] = useState('relevance');
  const [bestOnly, setBestOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(null);
  const [filters, setFilters] = useSearchFilterParams(searchParams, setSearchParams);
  const [categories, setCategories] = useState([]);
  useEffect(() => { categoriesAPI.getAll().then(setCategories).catch(() => {}); }, []);

  useEffect(() => {
    if (!query && !manufacturer && !equivBrand) { setProducts([]); setIsLoading(false); return; }
    let cancelled = false;
    setIsLoading(true);
    const params = vehicleStore.toParams(vehicle);
    Promise.all([
      productsAPI.getAll(null, query || null, null, manufacturer || null, equivBrand || null),
      params ? productsAPI.getAll(null, query || null, params, manufacturer || null, equivBrand || null) : Promise.resolve(null),
    ]).then(([all, compatible]) => {
      if (cancelled) return;
      setProducts(all);
      setCompatibleIds(compatible ? new Set(compatible.map(p => p.id)) : null);
      ga.search(query || manufacturer || equivBrand, 'parts', { search_results: all.length, ...(compatible ? { search_results_compatible: compatible.length } : {}), ...(manufacturer ? { search_brand: manufacturer } : {}) });
      if (query) productsAPI.logSearch({ q: query, results: all.length, compatible: compatible ? compatible.length : null, vehicle: vehicle ? { brand: vehicle.brand, model: vehicle.model, engine: vehicle.engine } : null, lang, visitor: consent.analytics() && window.posthog?.get_distinct_id ? window.posthog.get_distinct_id() : null });
    }).catch(console.error).finally(() => !cancelled && setIsLoading(false));
    return () => { cancelled = true; };
  }, [query, manufacturer, equivBrand, vehicle]);

  const handleAddToCart = async (productId) => {
    setAddingToCart(productId);
    try {
      await cartAPI.addItem(productId);
    } finally {
      setAddingToCart(null);
    }
  };

  const base = compatibleIds && !showAll ? products.filter(p => compatibleIds.has(p.id)) : products;
  const tokens = searchTokens(query);
  const isDirect = (p) => !tokens.length || [p.name, ...Object.values(p.name_i18n || {}), p.brand, p.ref, ...(p.refs || []), ...(p.oem_refs || []), p.category, p.subcategory, p.type].some(f => matchesTokens(f, tokens));
  const filtered = applySearchFilters(base, filters);
  const bestCount = filtered.filter(isBestPrice).length;
  const topRatedFilter = useTopRatedFilter(bestOnly ? filtered.filter(isBestPrice) : filtered);
  const visible = sortProducts(topRatedFilter.list, sort, filters.fuel || vehicleEnergy(vehicle?.engine?.fuel));
  const equivalents = new Set(visible.filter(p => !isDirect(p)).map(p => p.id));
  usePageMeta({
    title: manufacturer ? `${t('search.parts', { b: manufacturer.toUpperCase() })}${query ? ` « ${query} »` : ''}` : query ? t('search.metaQuery', { q: query }) : t('search.metaDefault'),
    description: t('search.metaDesc', { q: query ? `« ${query} »` : manufacturer, n: products.length }),
    noindex: true,
  });

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/" className="flex items-center gap-2 text-blue-600 hover:underline">
            <ArrowLeft size={18} /> {t('common.home')}
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3 flex-wrap" data-testid="search-title">
            {manufacturer ? (
              <>
                <BrandLogo brand={manufacturer.toUpperCase()} size="md" showName={false} testId="search-brand-logo" />
                <span>{t('search.parts', { b: manufacturer.toUpperCase() })}{query ? <> · « {query} »</> : null}</span>
                <Link to={`/marque/${encodeURIComponent(manufacturer.toUpperCase())}`} className="text-sm font-normal text-blue-600 hover:underline" data-testid="search-brand-page-link">{t('search.brandPage')}</Link>
              </>
            ) : equivBrand ? (
              <>
                <span data-testid="search-equiv-title">{t('search.equivBrand', { b: equivBrand.toUpperCase() })}{query ? <> · « {query} »</> : null}</span>
              </>
            ) : query ? <>{t('search.resultsFor', { q: query })}</> : t('search.title')}
          </h1>
          {equivBrand && <p className="text-sm text-gray-500 mt-1" data-testid="search-equiv-hint">{t('search.equivHint', { b: equivBrand.toUpperCase() })}</p>}
          <p className="text-gray-500 mt-1" data-testid="search-count">{t('cat.found', { n: visible.length, count: visible.length })}</p>
        </div>

        <VehicleBanner
          vehicle={vehicle}
          count={compatibleIds ? compatibleIds.size : undefined}
          showAll={showAll}
          onToggleShowAll={() => setShowAll(v => !v)}
        />

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : base.length === 0 ? (
          <SearchNoResults query={query} vehicle={vehicle}>
            {compatibleIds && !showAll && products.length > 0 && (
              <button onClick={() => setShowAll(true)} className="mt-4 text-blue-600 hover:underline text-sm" data-testid="search-show-unfiltered">
                {t('search.showUnfiltered', { n: products.length })}
              </button>
            )}
          </SearchNoResults>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <SearchFilters products={base} filters={filters} onChange={setFilters} categories={categories} resultCount={visible.length} hideBrand={!!manufacturer} />
            <div className="lg:col-span-3">
            <SortSelect value={sort} onChange={setSort} count={visible.length} bestPrice={{ total: bestCount, enabled: bestOnly, onToggle: () => setBestOnly(v => !v) }} topRated={topRatedFilter.topRated} />
            {equivalents.size > 0 && (
              <p className="text-xs text-gray-500 -mt-2 mb-3 flex items-center gap-1.5" data-testid="search-equivalents-note">
                <Repeat size={13} className="text-blue-600" /> {equivalents.size} équivalent{equivalents.size > 1 ? 's' : ''} d'autres marques (même référence OEM) inclus dans les résultats.
              </p>
            )}
            {visible.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center" data-testid="search-filters-empty">
                <p className="text-gray-600">{t('search.noneWithFilters')}</p>
                <button onClick={() => setFilters(EMPTY_SEARCH_FILTERS)} className="mt-3 text-blue-600 hover:underline text-sm" data-testid="search-filters-empty-reset">{t('cat.resetFilters')}</button>
              </div>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="search-results-grid">
              {visible.map(product => (
                <ProductCard
                  key={product.id}
                  product={product}
                  compatible={compatibleIds ? compatibleIds.has(product.id) : false}
                  onAddToCart={handleAddToCart}
                  isAdding={addingToCart === product.id}
                  highlightTokens={tokens}
                  equivalent={equivalents.has(product.id)}
                />
              ))}
            </div>
            )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default SearchPage;
